import { backendEndpoints } from '$/services/endpoints';
import { fetcher } from '$/services/fetcher';
import { searchMaterials } from '$/services/search/features/materials';
import {
  GetHarmoniesResponseSchema,
  GetHarmonyByIdResponseSchema,
} from '$/services/usecases/materials/mapper/harmonies';
import {
  type Material,
  type MaterialType,
} from '$/services/usecases/materials/mapper/materials';

export type MaterialBrightnessFilter = [from: number, to: number];
export type MaterialFilterOption = {
  option: string;
  value: string | number | (string | number)[];
};
export type SingleMaterialFilterOption = {
  option: string;
  value: string | number;
};

export interface GetMaterialsPayload {
  ids?: string[];
  query?: string;
  skip?: number;
  limit?: number;
  collectionId?: string;
  filter?: MaterialFilterOption[];
  brightnessFilter?: MaterialBrightnessFilter;
  page?: number;
  type?: MaterialType[] | MaterialType;
  order?: string;
  isDemo?: boolean;
}

export const getMaterials = async (
  opt: GetMaterialsPayload,
  signal?: AbortSignal,
) => {
  const alreadyHasCollectionFilter = opt.filter?.some(
    (f) => f.option === 'collection',
  );

  const hasForcedCollectionFilter =
    opt.collectionId && !alreadyHasCollectionFilter;

  const materials = await searchMaterials({
    signal,
    filter: [
      ...(opt.filter ?? []),
      ...(hasForcedCollectionFilter
        ? [{ option: 'collection', value: opt.collectionId! }]
        : []),
      ...(opt.ids ? [{ option: 'id', value: opt.ids }] : []),
      ...(opt.page ? [{ option: 'page', value: opt.page }] : []),
      ...(opt.type ? [{ option: 'type', value: opt.type }] : []),
      ...(opt.brightnessFilter &&
      (opt.brightnessFilter[0] !== 0 || opt.brightnessFilter[1] !== 100)
        ? [
            {
              option: 'brightness',
              value: opt.brightnessFilter,
            },
          ]
        : []),
    ],
    limit: opt.limit ?? 50,
    query: opt.query !== '' ? opt.query : undefined,
    order: hasForcedCollectionFilter ? 'page asc, pagePosition asc' : opt.order,
    skip: opt.skip,
    isDemo: opt.isDemo,
  });

  return {
    count: materials?.count ?? 0,
    results: (materials?.results ?? []) as Material[],
  };
};

export const getHarmonies = async (signal?: AbortSignal) => {
  return fetcher(`${backendEndpoints.MATERIAL_URL}/harmonies`, {
    method: 'GET',
    includeCredentials: true,
    parser: (data) => GetHarmoniesResponseSchema.parse(data),
    signal,
  });
};

export const getHarmonyById = async (id: string, signal?: AbortSignal) => {
  const res = await fetcher(
    `${backendEndpoints.MATERIAL_URL}/harmonies/${encodeURIComponent(id)}`,
    {
      method: 'GET',
      includeCredentials: true,
      parser: (data) => GetHarmonyByIdResponseSchema.parse(data),
      signal,
    },
  );

  if (!res.isSuccessful) return null;

  return res.response.payload ?? null;
};
